// Service page content for Skills Assessment.
// Sourced from docs/research/australian-visa-pathways.md §10.
// COMPLIANCE: we are not a registered migration agent and do not give migration
// advice, choose the occupation, or assess eligibility; those are a registered
// migration agent's. Our role is assembling and formatting the evidence the
// authority requires. No guaranteed outcomes; honest that a negative assessment
// can defeat an application; no unverified fee figures stated as fact. "Who does
// what" disclosure and disclaimer are rendered by the shared page template.

const skillsAssessment = {
  groupSlug: "skilled-migration",
  slug: "skills-assessment",
  title: "Skills Assessment",
  eyebrow: "Skilled Migration",
  intro:
    "A skills assessment is a formal check, by a government-appointed assessing authority, that your qualifications and experience meet the Australian standard for your occupation. A positive assessment is a prerequisite for almost every skilled visa, so getting it right is the foundation of your whole application.",

  chips: [
    { label: "Role", value: "Prerequisite for skilled visas" },
    { label: "Assessed by", value: "Occupation's assessing authority" },
    { label: "Tied to", value: "Your ANZSCO occupation" },
    { label: "Validity", value: "Time-limited (confirm per authority)" },
  ],

  whoFor: [
    "Anyone applying for a 189, 190, or 491, and many employer-sponsored and graduate applicants.",
    "People who have their occupation confirmed by a registered migration agent and want the evidence prepared properly for the assessing authority.",
    "Applicants who want their qualifications and experience organised in the strongest, most accurate way.",
  ],

  essentials: [
    {
      title: "It is occupation-specific",
      detail:
        "Each occupation maps to exactly one assessing authority. You cannot choose the authority; your occupation decides it.",
    },
    {
      title: "The right occupation code matters",
      detail:
        "Your ANZSCO occupation must genuinely match your skills and experience, and it shapes which visas are open to you.",
    },
    {
      title: "Each authority has its own rules",
      detail:
        "Criteria, fees, processing times, and validity periods differ. For example, ICT goes to ACS, engineering to Engineers Australia, trades to TRA, and many professions to VETASSESS.",
    },
    {
      title: "Some pathways need extra steps",
      detail:
        "Engineers without accredited qualifications may need a Competency Demonstration Report, and some trades use a Job Ready Program.",
    },
    {
      title: "Health professions need registration too",
      detail:
        "Nurses, doctors, and similar roles usually also need registration through AHPRA, which is separate from the skills assessment.",
    },
    {
      title: "Assessments expire",
      detail:
        "A skills assessment must be valid when your visa is lodged or decided, so timing it correctly matters.",
    },
  ],

  process: [
    {
      title: "Confirm the right occupation",
      detail:
        "A registered migration agent matches your qualifications and experience to the most suitable, defensible ANZSCO occupation.",
    },
    {
      title: "Identify the assessing authority",
      detail:
        "Once the occupation is confirmed, we work out which authority assesses it and what it requires.",
    },
    {
      title: "Prepare your evidence",
      detail:
        "We assemble your qualifications, references, and employment evidence in the format the authority expects.",
    },
    {
      title: "Lodge the assessment",
      detail:
        "We submit your assessment application and organise any requests for further information.",
    },
    {
      title: "Plan your visa around the result",
      detail:
        "With a positive assessment in hand, a registered migration agent maps your skilled visa strategy, and we keep the file moving.",
    },
  ],

  faqs: [
    {
      q: "Why do I need a skills assessment?",
      a: "For most skilled visas you cannot even submit an Expression of Interest without a positive skills assessment for your nominated occupation. It is the gateway step, and the rest of your application depends on it.",
    },
    {
      q: "Can I pick which authority assesses me?",
      a: "No. The assessing authority is determined by your occupation, not by preference. A registered migration agent chooses the occupation that best and most accurately reflects your background, which in turn sets the authority. We then prepare the evidence that authority requires.",
    },
    {
      q: "How long does it take and what does it cost?",
      a: "It varies by authority, and both the fees and processing times are set by the assessing authority, not by us. We give you a realistic estimate for your occupation and build the assessment time and cost into your overall plan.",
    },
    {
      q: "What if my assessment comes back negative?",
      a: "A negative or downgraded assessment can undermine an otherwise strong application, which is exactly why preparation matters. A registered migration agent assesses your prospects honestly first, and we organise and prepare your evidence so your best case goes forward.",
    },
  ],

  related: [
    "skilled-independent-189",
    "skilled-nominated-190",
    "skilled-regional-491",
  ],

  lastVerified: "13 July 2026",
};

export default skillsAssessment;
